import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import styles from './Stepper.module.css';

export type StepperStep = {
  readonly id: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly icon?: ReactNode;
};

export type StepperProps = {
  label: string;
  steps: readonly StepperStep[];
  current: number;
  reachable?: number | undefined;
  onSelect: (index: number) => void;
};

type StepState = 'done' | 'current' | 'upcoming';

const stateOf = (index: number, current: number): StepState => {
  if (index === current) return 'current';
  return index < current ? 'done' : 'upcoming';
};

export const Stepper = ({ label, steps, current, reachable = current, onSelect }: StepperProps) => (
  <ol className={styles['steps']} aria-label={label}>
    {steps.map((step, index) => {
      const state = stateOf(index, current);
      return (
        <li key={step.id} className={styles['item']}>
          <button
            type="button"
            className={styles['step']}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
            disabled={index > reachable}
            onClick={() => {
              onSelect(index);
            }}
          >
            <span className={styles['marker']} aria-hidden="true">
              {state === 'done' ? (
                <Check size={16} strokeWidth={2.5} aria-hidden="true" />
              ) : (
                (step.icon ?? index + 1)
              )}
            </span>
            <span className={styles['text']}>
              <span className={styles['title']}>{step.title}</span>
              {step.description ? <span className={styles['description']}>{step.description}</span> : null}
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);
